import { Shield, Brain, FileSearch, AlertTriangle, Search, Handshake } from "lucide-react";

const shields = [
  {
    icon: Shield,
    title: "Cyber Shield Cup",
    game: "Phish Dhurandhar",
    activity: "Reads emails",
    accent: "#dc2626",
  },
  {
    icon: Brain,
    title: "Decision Shield Cup",
    game: "Decision Dhurandhar",
    activity: "Makes decisions",
    accent: "#f59e0b",
  },
  {
    icon: FileSearch,
    title: "Information Shield Cup",
    game: "Fact or Fake Dhurandhar",
    activity: "Reads information",
    accent: "#dc2626",
  },
  {
    icon: AlertTriangle,
    title: "Risk Shield Cup",
    game: "Risk Dhurandhar",
    activity: "Identifies risks",
    accent: "#f59e0b",
  },
  {
    icon: Search,
    title: "Intelligence Cup",
    game: "Clue Dhurandhar",
    activity: "Finds clues",
    accent: "#dc2626",
  },
  {
    icon: Handshake,
    title: "Trust Shield Cup",
    game: "Trust Dhurandhar",
    activity: "Communicates",
    accent: "#f59e0b",
  },
];

export default function Shields() {
  return (
    <section
      id="shields"
      data-testid="shields-section"
      className="relative py-28 lg:py-36 border-b border-white/5 bg-black"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#dc2626] mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-[#dc2626]" /> 03 · The Dhurandhar Shield Series
          </div>
          <h2 className="font-display uppercase font-black text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            Six championships.
            <br />
            <span className="text-neutral-500">One universal</span> skill engine.
          </h2>
          <p className="mt-6 text-neutral-400 leading-relaxed max-w-2xl">
            Each Shield Cup is built on the same Dhurandhar engine — a story-driven,
            pressure-tested format pointed at different everyday workplace behaviours.
          </p>
        </div>

        {/* Control-room grid: 1px borders */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
          {shields.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                data-testid={`shield-card-${i}`}
                className="group relative border-r border-b border-white/10 p-8 lg:p-10 hover:bg-[#0a0a0a] transition-colors"
              >
                <div className="absolute top-0 left-0 font-mono text-[10px] text-neutral-600 p-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex items-start gap-4 mb-8 mt-6">
                  <div
                    className="w-11 h-11 border flex items-center justify-center"
                    style={{ borderColor: s.accent, color: s.accent }}
                  >
                    <Icon size={20} />
                  </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                  Activity · {s.activity}
                </div>
                <h3 className="font-display uppercase font-black text-2xl lg:text-3xl tracking-tight leading-tight">
                  {s.title}
                </h3>
                <div className="mt-4 font-mono text-xs text-neutral-400">
                  <span className="text-[#f59e0b]">▸</span> {s.game}
                </div>

                {/* Corner accent on hover */}
                <div
                  className="absolute top-0 right-0 w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: s.accent }}
                />
              </div>
            );
          })}
        </div>

        {/* Featured: Cyber Shield Cup */}
        <div
          data-testid="cyber-shield-feature"
          className="mt-12 border border-white/10 bg-gradient-to-br from-[#0a0a0a] to-black p-8 md:p-12 grid grid-cols-12 gap-8"
        >
          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#dc2626] mb-4">
              SPOTLIGHT · Cyber Shield Cup
            </div>
            <h3 className="font-display uppercase font-black text-3xl lg:text-4xl leading-[0.95]">
              The complete <span className="text-[#dc2626]">human-risk</span> engine.
            </h3>
            <p className="mt-5 text-neutral-400 max-w-md">
              91% of cyberattacks begin with a phishing email. Employees click 1 in 3.
              Phish Dhurandhar is 3× more effective than standard CBT — with zero extra
              infrastructure.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              "Phishing Detection",
              "BEC Challenge",
              "QR Code Scam",
              "USB Drop",
              "Password Security",
              "Social Engineering",
              "Incident Response",
              "The Deception Brief",
            ].map((c, i) => (
              <div
                key={i}
                className="bg-black p-4 min-h-[100px] flex items-end"
              >
                <div>
                  <div className="font-mono text-[10px] text-[#f59e0b]">
                    CH · {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-1 text-white text-sm font-medium leading-snug">
                    {c}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
